import type {FilterSuggestion, SuppliedFilterProviderOptions} from '@github-ui/filter-core'
import {NOT_SHOWN} from '@github-ui/filter-core'
import {DateFilterProvider, SelectFilterProvider} from '@github-ui/filter-core/providers'
import {AlertIcon, AppsIcon, CalendarIcon, LinkIcon, RocketIcon} from '@primer/octicons-react'

// --- Artifact Registry ---

const ARTIFACT_REGISTRY_FILTER_KEY = {
  displayName: 'Registry',
  key: 'artifact-registry',
  description: 'Filter by artifact registry',
  priority: 1,
  icon: AppsIcon,
}

const ARTIFACT_REGISTRY_VALUES: FilterSuggestion[] = [{value: 'jfrog-artifactory', priority: 1}]

const artifactRegistryFilterOptions: SuppliedFilterProviderOptions = {
  filterTypes: {multiValue: false, hasValue: false, valueless: false},
}

export class ArtifactRegistryFilterProvider extends SelectFilterProvider {
  constructor() {
    super(ARTIFACT_REGISTRY_FILTER_KEY, ARTIFACT_REGISTRY_VALUES, artifactRegistryFilterOptions)
  }
}

// --- Deployments ---

const HAS_DEPLOYMENTS_FILTER_KEY = {
  displayName: 'Deployments',
  key: 'deployments',
  description: 'Filter by deployment presence',
  priority: NOT_SHOWN,
  icon: RocketIcon,
}

/**
 * Hidden provider that contributes `has:deployments` to the auto-generated
 * has-filter. Priority is NOT_SHOWN so `deployments:` never appears as a
 * standalone suggestion.
 */
export class HasDeploymentsFilterProvider extends SelectFilterProvider {
  constructor() {
    super(HAS_DEPLOYMENTS_FILTER_KEY, [], {
      priority: NOT_SHOWN,
      filterTypes: {multiValue: false},
    })
  }
}

// --- Runtime Risk ---

const RUNTIME_RISK_FILTER_KEY = {
  displayName: 'Runtime risk',
  key: 'runtime-risk',
  description: 'Filter by runtime risk category',
  priority: 2,
  icon: AlertIcon,
}

const RUNTIME_RISK_VALUES: FilterSuggestion[] = [
  {value: 'critical-resource', priority: 1},
  {value: 'internet-exposed', priority: 2},
  {value: 'sensitive-data', priority: 3},
  {value: 'lateral-movement', priority: 4},
]

export class RuntimeRiskFilterProvider extends SelectFilterProvider {
  constructor() {
    super(RUNTIME_RISK_FILTER_KEY, RUNTIME_RISK_VALUES, {
      filterTypes: {valueless: false, hasValue: false},
    })
  }
}

// --- Artifact Registry URL ---

const ARTIFACT_REGISTRY_URL_FILTER_KEY = {
  displayName: 'Registry URL',
  key: 'artifact-registry-url',
  description: 'Filter by artifact registry URL',
  priority: 3,
  icon: LinkIcon,
}

export class ArtifactRegistryUrlFilterProvider extends SelectFilterProvider {
  constructor() {
    super(ARTIFACT_REGISTRY_URL_FILTER_KEY, [], {
      filterTypes: {multiValue: false, valueless: false, hasValue: false},
      strictMatching: false,
    })
  }
}

// --- Date filters ---

const DATE_VALUES: FilterSuggestion[] = [
  {value: '@today', displayName: 'Today', priority: 1},
  {value: '@today-1d', displayName: 'Yesterday', priority: 2},
  {value: '>@today-1w', displayName: 'Past 7 days', priority: 3},
  {value: '>@today-30d', displayName: 'Past 30 days', priority: 4},
  {value: '>@today-1y', displayName: 'Past year', priority: 5},
]

const CREATED_FILTER_KEY = {
  displayName: 'Created',
  key: 'created',
  description: 'Filter by creation date',
  priority: 4,
  icon: CalendarIcon,
}

export class CreatedFilterProvider extends DateFilterProvider {
  constructor() {
    super(CREATED_FILTER_KEY, DATE_VALUES, {
      priority: CREATED_FILTER_KEY.priority,
      filterTypes: {valueless: false, hasValue: false},
    })
  }
}

const UPDATED_FILTER_KEY = {
  displayName: 'Updated',
  key: 'updated',
  description: 'Filter by last updated date',
  priority: 5,
  icon: CalendarIcon,
}

export class UpdatedFilterProvider extends DateFilterProvider {
  constructor() {
    super(UPDATED_FILTER_KEY, DATE_VALUES, {
      priority: UPDATED_FILTER_KEY.priority,
      filterTypes: {valueless: false, hasValue: false},
    })
  }
}
